/**
 * useStagedFiles — the files staged in the content chat's composer: picking
 * (checked at pick time against the server's file policy, each refusal
 * toasted), removing, and the chips' upload state while a send runs.
 *
 * Preview-URL lifetime (as for a staged frame): a staged
 * image's preview is revoked when the user removes it, and on unmount — never
 * when its send succeeds, and never while a send showing it is in flight,
 * because the optimistic bubble is still painting it. One removed mid-send is
 * released once that send fails or is dropped: its bubble comes down.
 *
 * Upload progress: a file's `progress` says whether its chip shows a ring and
 * where the ring starts; every later tick goes to `uploadProgress`, which the
 * chip reads on its own, so a tick never re-renders the composer's owners.
 */
import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import { toast } from 'sonner';

import { newLocalId } from '@discovr/features';
import type { ThreadFilePolicy } from '@discovr/types';

import { validatePicks, type StagedFile } from '../../../../../lib/media/pickFiles';
import type { StagedFileState, UploadProgressSource } from '../../../ChatThread';
import { fileInFlight, toWholePercent, uploadProgressed, type SendAttempt, type StagedFileUpdate } from './sendDraft';
import { createUploadProgressStore, type UploadProgressStore } from './uploadProgressStore';
import type { ObjectUrlRegistry } from './useObjectUrlRegistry';

export interface StagedFilesStore {
  files: StagedFileState[];
  /** The files as they are now — for async work that outlives the render it started in. */
  filesRef: MutableRefObject<StagedFileState[]>;
  /** Stages the picked files that pass `policy`'s checks and returns them; toasts one refusal per file that doesn't. */
  pickFiles(picked: File[], policy: ThreadFilePolicy): StagedFile[];
  /** × on a chip: unstages the file and releases its preview. */
  removeFile(id: string): void;
  /** Unstages every file and releases their previews — except those a send in flight is still showing. */
  clearFiles(): void;
  /** Rewrites every staged file's chip state (upload progress, failed mark); a no-op when nothing changes. */
  updateFiles(update: StagedFileUpdate): void;
  /** The chips' live upload progress — what `showProgress` moves. */
  uploadProgress: UploadProgressSource;
  /** An upload tick: puts the file's ring up if it has none yet, else moves it without a render here. */
  showProgress(id: string, fraction: number): void;
  /** Unstages the files a send carried; their previews stay alive for the sent bubble. */
  releaseFiles(sent: StagedFile[]): void;
  /** A send that didn't go out: releases the previews of its files the user has removed meanwhile. */
  releaseRemovedPreviews(unsent: StagedFile[]): void;
}

function showRefusal(refusal: string): void {
  toast.error(refusal);
}

function hasId(files: StagedFile[], id: string): boolean {
  return files.some((file) => file.id === id);
}

function sameFiles(current: StagedFileState[], next: StagedFileState[]): boolean {
  return next.every((file, index) => file === current[index]);
}

/**
 * A file whose ring (re)started, stopped or went away drops its live value, so
 * its chip shows the `progress` it now has rather than a tick from before.
 */
function forgetChangedRings(progress: UploadProgressStore, current: StagedFileState[], next: StagedFileState[]): void {
  const nextById = new Map(next.map((file) => [file.id, file]));
  current.forEach((file) => {
    const updated = nextById.get(file.id);
    if (updated === file || updated?.progress === file.progress) return;
    progress.forget(file.id);
  });
}

export function useStagedFiles(urls: ObjectUrlRegistry, sendingRef: MutableRefObject<Set<SendAttempt>>): StagedFilesStore {
  const [files, setFiles] = useState<StagedFileState[]>([]);
  const filesRef = useRef<StagedFileState[]>(files);
  const [uploadProgress] = useState(createUploadProgressStore);

  const commitFiles = useCallback(
    (next: StagedFileState[]) => {
      forgetChangedRings(uploadProgress, filesRef.current, next);
      filesRef.current = next;
      setFiles(next);
    },
    [uploadProgress],
  );

  const pickFiles = useCallback(
    (picked: File[], policy: ThreadFilePolicy): StagedFile[] => {
      const { accepted, refusals } = validatePicks(picked, { policy, alreadyStaged: filesRef.current.length, newId: newLocalId });
      refusals.forEach(showRefusal);
      if (accepted.length === 0) return [];
      accepted.forEach((file) => urls.adopt(file.previewUrl));
      commitFiles([...filesRef.current, ...accepted]);
      return accepted;
    },
    [urls, commitFiles],
  );

  const removeFile = useCallback(
    (id: string) => {
      const removed = filesRef.current.find((file) => file.id === id);
      if (!removed) return;
      commitFiles(filesRef.current.filter((file) => file.id !== id));
      if (fileInFlight(sendingRef.current, id)) return;
      urls.revoke(removed.previewUrl);
    },
    [urls, sendingRef, commitFiles],
  );

  const clearFiles = useCallback(() => {
    function isUnshown(file: StagedFile): boolean {
      return !fileInFlight(sendingRef.current, file.id);
    }
    const cleared = filesRef.current;
    commitFiles([]);
    cleared.filter(isUnshown).forEach((file) => urls.revoke(file.previewUrl));
  }, [urls, sendingRef, commitFiles]);

  const updateFiles = useCallback(
    (update: StagedFileUpdate) => {
      const next = filesRef.current.map(update);
      if (sameFiles(filesRef.current, next)) return;
      commitFiles(next);
    },
    [commitFiles],
  );

  const showProgress = useCallback(
    (id: string, fraction: number) => {
      const file = filesRef.current.find((staged) => staged.id === id);
      if (!file) return;
      if (file.progress === undefined) {
        updateFiles(uploadProgressed(id, fraction));
        return;
      }
      uploadProgress.set(id, toWholePercent(fraction));
    },
    [updateFiles, uploadProgress],
  );

  const releaseFiles = useCallback(
    (sent: StagedFile[]) => commitFiles(filesRef.current.filter((file) => !hasId(sent, file.id))),
    [commitFiles],
  );

  const releaseRemovedPreviews = useCallback(
    (unsent: StagedFile[]) => {
      function isRemovedAndUnshown(file: StagedFile): boolean {
        return !hasId(filesRef.current, file.id) && !fileInFlight(sendingRef.current, file.id);
      }
      unsent.filter(isRemovedAndUnshown).forEach((file) => urls.revoke(file.previewUrl));
    },
    [urls, sendingRef],
  );

  return { files, filesRef, pickFiles, removeFile, clearFiles, updateFiles, uploadProgress, showProgress, releaseFiles, releaseRemovedPreviews };
}
